import { LoadingButton } from '@mui/lab';
import ArticleIcon from '@mui/icons-material/Article';
import DescriptionIcon from '@mui/icons-material/Description';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import MoreVert from '@mui/icons-material/MoreVert';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import {
    Avatar,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Container,
    Divider,
    Grid,
    IconButton,
    ListItem,
    Menu,
    MenuItem,
    Paper,
    Stack,
    Tab,
    TextField,
    Typography,
} from '@mui/material';
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import MyLocation from '@mui/icons-material/MyLocation';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { Link } from 'react-router-dom';
import { useCookies } from 'react-cookie';
import { useEffect, useMemo, useState } from 'react';
import AddImage from '../../../components/AddImage';
import AdvertiserUserAssignmentForm from '../components/AdvertiserUserAssignmentForm';
import { canManageAdvertiserUsers } from '../utils/canManageAdvertiserUsers';
import Dropdown from '../../../components/Dropdown';
import { LoadingContent } from '../../../components/Loading';
import { useFetch } from '../../../hooks/useFecth';
import server from '../../../services/server';
import useErrors from '../../../hooks/useErrors';

const advertiserDataSchema = yup.object().shape({
    name: yup.string().required('Informe o nome fantasia'),
    businessName: yup.string(),
    document: yup.string(),
    email: yup.string().email('E-mail inválido'),
    description: yup.string(),
    zipcode: yup.string(),
    street: yup.string(),
    number: yup.string(),
    complement: yup.string(),
    neighborhood: yup.string(),
    city: yup.string(),
    state: yup.string(),
    phone: yup.string(),
    latitude: yup.string().trim().optional(),
    longitude: yup.string().trim().optional(),
});

function AdvertiserContractPanel({ advertiser }) {
    const contract = advertiser?.contract;
    const signedPdfUrl =
        contract?.Uploads?.find(i => i.description === 'contrato_assinado')?.fileUrl ||
        contract?.Uploads?.find(i => i.description === 'contrato_original')?.fileUrl;
    const hasContractSigned =
        Boolean(contract?.signedAt) || ['CONCLUDED', 'SIGNED'].includes(contract?.status);

    return (
        <Paper sx={{ p: 2 }}>
            <Stack spacing={2}>
                <Typography variant="h4">Contrato</Typography>
                <Divider />
                {!contract ? (
                    <Typography variant="body2" color="text.secondary">
                        Nenhum contrato vinculado a este anunciante.
                    </Typography>
                ) : (
                    <>
                        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
                            <Typography variant="body2" color="text.secondary">
                                Status:
                            </Typography>
                            <Chip
                                size="small"
                                label={contract.status || (hasContractSigned ? 'Assinado' : 'Pendente')}
                                color={hasContractSigned ? 'success' : 'default'}
                                variant="outlined"
                            />
                            {contract.signedAt ? (
                                <Typography variant="body2" color="text.secondary">
                                    Assinado em {new Date(contract.signedAt).toLocaleString('pt-BR')}
                                </Typography>
                            ) : null}
                        </Stack>
                        {contract.id != null ? (
                            <Typography variant="body2" color="text.secondary">
                                Contrato #{contract.id}
                            </Typography>
                        ) : null}
                        {signedPdfUrl ? (
                            <Button variant="contained" component="a" href={signedPdfUrl} target="_blank" rel="noopener noreferrer" download>
                                Baixar contrato (PDF)
                            </Button>
                        ) : (
                            <Typography variant="body2" color="text.secondary">
                                O PDF do contrato assinado ainda não está disponível nos uploads.
                            </Typography>
                        )}
                    </>
                )}
            </Stack>
        </Paper>
    );
}

/** Dados da empresa / anunciante (GET/PUT `/advertiser/profile`) — layout alinhado ao painel administrativo. */
export default function AdvertiserData() {
    const { data: profile, mutate, isFetching, error } = useFetch('/advertiser/profile');
    const [{ user }] = useCookies(['user']);
    const { setErrors, enqueueSnackbar } = useErrors();
    const [tab, setTab] = useState('#tab-1');
    const [locating, setLocating] = useState(false);
    const canManageUsers = canManageAdvertiserUsers(user);
    const { data: usersList, mutate: mutateUsers } = useFetch(canManageUsers ? '/advertiser/users' : null, {
        params: { perPage: 100, page: 0 },
    });

    useEffect(() => {
        if (!canManageUsers && tab === '#tab-user') {
            setTab('#tab-1');
        }
    }, [canManageUsers, tab]);

    const planName = profile?.contract?.subscription?.Plan?.name;

    const myAssignment = useMemo(() => {
        const rows = usersList?.data;
        if (!user?.userName || !Array.isArray(rows) || rows.length === 0) return null;
        const u = String(user.userName).toLowerCase();
        return rows.find(a => String(a.user?.userName || '').toLowerCase() === u) ?? null;
    }, [usersList?.data, user?.userName]);

    const formik = useFormik({
        enableReinitialize: true,
        validationSchema: advertiserDataSchema,
        initialValues: {
            name: profile?.name || '',
            businessName: profile?.businessName || '',
            image: profile?.image || '',
            previewImage: null,
            document: profile?.document || '',
            email: profile?.email || '',
            description: profile?.description || '',
            zipcode: profile?.zipcode || '',
            street: profile?.street || '',
            number: profile?.number || '',
            complement: profile?.complement || '',
            neighborhood: profile?.neighborhood || '',
            city: profile?.city || '',
            state: profile?.state || '',
            phone: profile?.phone || '',
            phone2: profile?.phone2 || '',
            responsibleName: profile?.responsibleName || '',
            responsibleEmail: profile?.responsibleEmail || '',
            responsiblePhone: profile?.responsiblePhone || '',
            website: profile?.website || '',
            notes: profile?.notes || '',
            latitude: profile?.latitude != null ? String(profile.latitude) : '',
            longitude: profile?.longitude != null ? String(profile.longitude) : '',
        },
        onSubmit: async vals => {
            try {
                const { previewImage: _preview, ...body } = vals;
                await server.put('/advertiser/profile', body);
                await mutate();
                enqueueSnackbar('Dados salvos com sucesso.', { variant: 'success' });
            } catch (err) {
                setErrors(err);
            }
        },
    });

    const handlePasteCoords = e => {
        const text = e.clipboardData.getData('text');
        const parts = text.split(',').map(s => s.trim());
        if (parts.length === 2 && !Number.isNaN(Number(parts[0])) && !Number.isNaN(Number(parts[1]))) {
            formik.setFieldValue('latitude', parts[0]);
            formik.setFieldValue('longitude', parts[1]);
            e.preventDefault();
        }
    };

    const handleUseCurrentLocation = () => {
        if (!navigator.geolocation) {
            enqueueSnackbar('Geolocalização não disponível neste navegador.', { variant: 'warning' });
            return;
        }
        setLocating(true);
        navigator.geolocation.getCurrentPosition(
            ({ coords }) => {
                formik.setFieldValue('latitude', String(coords.latitude));
                formik.setFieldValue('longitude', String(coords.longitude));
                setLocating(false);
                enqueueSnackbar('Localização coletada. Salve as alterações para gravar.', { variant: 'success' });
            },
            err => {
                setLocating(false);
                const msg =
                    err?.code === 1
                        ? 'Permissão de localização negada.'
                        : 'Não foi possível obter a localização.';
                enqueueSnackbar(msg, { variant: 'error' });
            },
            { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
        );
    };

    if (isFetching && !profile) {
        return <LoadingContent />;
    }
    if (error) {
        return (
            <Typography color="error" variant="body1">
                Não foi possível carregar o perfil. Tente novamente mais tarde.
            </Typography>
        );
    }
    if (!profile) {
        return <Typography variant="body1">Tente novamente mais tarde.</Typography>;
    }

    const { handleSubmit, handleChange, values, errors, touched, isSubmitting, setFieldValue } = formik;
    const displayImageSrc = values.previewImage || values.image || profile?.image || undefined;

    return (
        <Container sx={{ gap: 3, my: 4, flexDirection: 'column', display: 'flex' }}>
            <Stack alignItems="center" direction="row" columnGap={1} py={3}>
                <IconButton component={Link} to="/anunciante" replace={false}>
                    <ChevronLeft />
                </IconButton>
                <Typography variant="h2">Detalhes da minha conta</Typography>
            </Stack>

            <Card>
                <CardContent>
                    <Stack direction="row" alignItems="center" columnGap={2} pb={2}>
                        <Avatar
                            variant="rounded"
                            sx={{ border: '1px solid #eee', objectFit: 'cover', width: 70, height: 70 }}
                            src={displayImageSrc}
                        />
                        <Box>
                            <Typography variant="h4">{values.name || profile?.name || '—'}</Typography>
                            <Typography variant="h6" sx={{ fontStyle: 'italic' }}>
                                {values.businessName || profile?.businessName || '—'}
                            </Typography>
                        </Box>

                        <Stack direction="row" gap={1} sx={{ ml: 'auto' }}>
                            <Dropdown>
                                <IconButton>
                                    <MoreVert />
                                </IconButton>
                                <Menu placement="bottom-end">
                                    <MenuItem
                                        onClick={() => {
                                            setTab('#tab-1');
                                            setTimeout(() => {
                                                document.getElementById('dados-empresa')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                            }, 0);
                                        }}
                                    >
                                        Alterar dados
                                    </MenuItem>
                                    {canManageUsers ? (
                                        <MenuItem onClick={() => setTab('#tab-user')}>Usuário e senha</MenuItem>
                                    ) : null}
                                    <Divider />
                                    <ListItem color="danger" sx={{ fontSize: 10 }}>
                                        ID Legado: {profile?.originId ?? '—'}
                                    </ListItem>
                                </Menu>
                            </Dropdown>
                        </Stack>
                    </Stack>
                    <Divider />
                    <Grid container columnGap={2} pt={2}>
                        <Grid item xs={12} sm={6} md textAlign="center">
                            <Typography variant="title-sm">Status</Typography>
                            <Box>
                                <Chip
                                    size="small"
                                    label={profile?.status ? 'Ativo' : 'Inativo'}
                                    color={profile?.status ? 'success' : 'error'}
                                    icon={
                                        <FiberManualRecordIcon htmlColor={profile?.status ? 'green' : 'grey'} />
                                    }
                                />
                            </Box>
                        </Grid>
                        <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' } }} />
                        <Grid item xs={12} sm={6} md>
                            <Typography variant="title-sm">Plano</Typography>
                            <Typography variant="text-sm">{planName || '—'}</Typography>
                        </Grid>
                        <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' } }} />
                        <Grid item xs={12} sm={6} md>
                            <Typography variant="title-sm">Responsável</Typography>
                            <Typography variant="subtitle-sm">
                                {values.responsibleName || profile?.responsibleName || '—'}
                            </Typography>
                        </Grid>
                        <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' } }} />
                        <Grid item xs={12} sm={6} md>
                            <Typography variant="title-sm">Contato</Typography>
                            <Typography variant="subtitle-sm">
                                {values.responsibleEmail || profile?.responsibleEmail || '—'}
                            </Typography>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            <TabContext value={tab}>
                <TabList onChange={(e, v) => setTab(v)}>
                    <Tab icon={<ArticleIcon />} iconPosition="start" label="Dados" value="#tab-1" />
                    {canManageUsers ? (
                        <Tab icon={<ManageAccountsIcon />} iconPosition="start" label="Usuário e senha" value="#tab-user" />
                    ) : null}
                    <Tab icon={<DescriptionIcon />} iconPosition="start" label="Contrato" value="#tab-5" />
                </TabList>
                <TabPanel value="#tab-1" sx={{ p: 0, pt: 2 }}>
                    <Stack id="dados-empresa" spacing={3} component="form" onSubmit={handleSubmit}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    Dados gerais
                                </Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <AddImage
                                            onChange={setFieldValue}
                                            name="image"
                                            value={values.previewImage || values.image}
                                            canvaWidth={300}
                                            canvaHeight={300}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="Nome fantasia"
                                            name="name"
                                            value={values.name}
                                            onChange={handleChange}
                                            error={touched.name && Boolean(errors.name)}
                                            helperText={touched.name && errors.name}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="Razão social"
                                            name="businessName"
                                            value={values.businessName}
                                            onChange={handleChange}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="Documento"
                                            name="document"
                                            value={values.document}
                                            onChange={handleChange}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="E-mail"
                                            name="email"
                                            value={values.email}
                                            onChange={handleChange}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label="Descrição da empresa"
                                            name="description"
                                            value={values.description}
                                            onChange={handleChange}
                                            multiline
                                            minRows={3}
                                            error={touched.description && Boolean(errors.description)}
                                            helperText={touched.description && errors.description}
                                        />
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    Endereço
                                </Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={3}>
                                        <TextField
                                            fullWidth
                                            label="CEP"
                                            name="zipcode"
                                            value={values.zipcode}
                                            onChange={handleChange}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="Rua"
                                            name="street"
                                            value={values.street}
                                            onChange={handleChange}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <TextField
                                            fullWidth
                                            label="Número"
                                            name="number"
                                            value={values.number}
                                            onChange={handleChange}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="Complemento"
                                            name="complement"
                                            value={values.complement}
                                            onChange={handleChange}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="Bairro"
                                            name="neighborhood"
                                            value={values.neighborhood}
                                            onChange={handleChange}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField fullWidth label="Cidade" name="city" value={values.city} onChange={handleChange} />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField fullWidth label="Estado" name="state" value={values.state} onChange={handleChange} />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1, lineHeight: 1.55 }}>
                                            Para a loja aparecer nas recomendações por proximidade, informe latitude e longitude —
                                            opcional. Cole as coordenadas no campo Latitude (ex.: -15.7801, -47.9292) ou use o botão
                                            abaixo estando no local da loja.
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="Latitude"
                                            name="latitude"
                                            value={values.latitude}
                                            onChange={handleChange}
                                            onPaste={handlePasteCoords}
                                            error={Boolean(touched.latitude && errors.latitude)}
                                            helperText={
                                                (touched.latitude && errors.latitude) ||
                                                'Cole as coordenadas aqui para preencher os dois campos'
                                            }
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="Longitude"
                                            name="longitude"
                                            value={values.longitude}
                                            onChange={handleChange}
                                            error={Boolean(touched.longitude && errors.longitude)}
                                            helperText={touched.longitude && errors.longitude}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <LoadingButton
                                            variant="outlined"
                                            startIcon={<MyLocation />}
                                            loading={locating}
                                            onClick={handleUseCurrentLocation}
                                            sx={{ textTransform: 'none' }}
                                        >
                                            Estou no local
                                        </LoadingButton>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    Contatos
                                </Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={4}>
                                        <TextField
                                            fullWidth
                                            label="Telefone principal"
                                            name="phone"
                                            value={values.phone}
                                            onChange={handleChange}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <TextField
                                            fullWidth
                                            label="Telefone 2"
                                            name="phone2"
                                            value={values.phone2}
                                            onChange={handleChange}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <TextField
                                            fullWidth
                                            label="Site"
                                            name="website"
                                            value={values.website}
                                            onChange={handleChange}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <TextField
                                            fullWidth
                                            label="Responsável"
                                            name="responsibleName"
                                            value={values.responsibleName}
                                            onChange={handleChange}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <TextField
                                            fullWidth
                                            label="E-mail do responsável"
                                            name="responsibleEmail"
                                            value={values.responsibleEmail}
                                            onChange={handleChange}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <TextField
                                            fullWidth
                                            label="Telefone do responsável"
                                            name="responsiblePhone"
                                            value={values.responsiblePhone}
                                            onChange={handleChange}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label="Observações"
                                            name="notes"
                                            multiline
                                            minRows={3}
                                            value={values.notes}
                                            onChange={handleChange}
                                        />
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>

                        <Stack direction="row" justifyContent="flex-end">
                            <LoadingButton type="submit" variant="contained" loading={isSubmitting}>
                                Salvar alterações
                            </LoadingButton>
                        </Stack>
                    </Stack>
                </TabPanel>
                {canManageUsers ? (
                    <TabPanel value="#tab-user" sx={{ p: 0, pt: 2 }}>
                        <AdvertiserUserAssignmentForm
                            key={myAssignment?.id ?? 'novo-usuario'}
                            variant="inline"
                            assignment={myAssignment}
                            onSuccess={() => mutateUsers()}
                        />
                    </TabPanel>
                ) : null}
                <TabPanel value="#tab-5" sx={{ p: 0 }}>
                    <AdvertiserContractPanel advertiser={profile} />
                </TabPanel>
            </TabContext>
        </Container>
    );
}
